import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { validateAuthForm } from './validateAuthForm';
import { IAuthErrors, ISignInResponse } from './types';
import { signIn, signIn2FA } from 'src/services/client';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { handleSignIn } from './handleSignIn';
import { toast } from 'react-toastify';
import { handleSessionExpired } from 'src/services/auth/handleSessionExpired';

export interface UseAuthReturn {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  termsAndConditions: boolean;
  setTermsAndConditions: Dispatch<SetStateAction<boolean>>;
  twoFACode: string;
  setTwoFACode: Dispatch<SetStateAction<string>>;
  errors: IAuthErrors;
  show2FA: boolean;
  loading: boolean;
  submit: () => Promise<void>;
  submitTwoFA: () => Promise<void>;
  signOut: () => Promise<boolean>;
    clearAuthStorage: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [loading, setLoading] = useState(true);
  const [twoFaToken, setTwoFaToken] = useState('');

  const [errors, setErrors] = useState<IAuthErrors>({
    email: {
      invalid: false,
      message: '',
    },
    password: {
      invalid: false,
      message: '',
    },
    termsAndConditions: {
      invalid: false,
      message: '',
    },
  });

  const dispatch = useDispatch();
  const history = useHistory();

  const clearAll = () => {
    setEmail('');
    setPassword('');
    setTermsAndConditions(false);
    setTwoFACode('');
    setShow2FA(false);
    setTwoFaToken('');
  };

  const validateForm = (): boolean => {
    const newErrors: IAuthErrors = validateAuthForm(
      email,
      password,
      termsAndConditions
    );

    setErrors(newErrors);

    const errorList: any[] = Object.values(newErrors);
    const hasInvalidField: boolean = errorList.some((error) => error.invalid);

    return !hasInvalidField;
  };

  const submit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const credentials = {
      Username: email.trim(),
      Password: password.trim(),
    };

    try {
      const res: any = await signIn(credentials);

      if (!(res instanceof Response)) {
        return;
      }

      const data = await res.json();

      if (res.ok && res.status === 200) {
        handleSignIn(data, dispatch, history);
        return;
      }

      if (res.status === 401 && data.data?.requiresTwoFactor) {
        setShow2FA(true);
        setTwoFaToken(data.data.auth);
        return;
      }

      toast.error('🚫 Login failed, invalid email or password', {
        autoClose: 12000,
        position: 'top-center',
      });
    } catch (error) {
      console.error('Sign-in error:', error);
      toast.error('Unknown Error, please try again later.', {
        autoClose: false,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFA = async (): Promise<void> => {
    setLoading(true);

    try {
      const res: any = await signIn2FA(twoFACode, twoFaToken);

      if (!(res instanceof Response)) {
        return;
      }

      const data = (await res.json()) as ISignInResponse;

      if (res.ok) {
        handleSignIn(data, dispatch, history);
        return;
      }

      toast.error(
        '🚫 Login failed, invalid two factor authentication code',
        {
          autoClose: 12000,
          position: 'top-center',
        }
      );

      clearAll();
      console.error('Two-factor authentication failed:', res);
    } catch (error) {
      console.error('Two-factor authentication error:', error);
      toast.error('Unknown Error, please try again later.', {
        autoClose: false,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearAuthStorage = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('auth');
    localStorage.removeItem('user');
    localStorage.setItem('isSignedOut', 'true');
    localStorage.removeItem('isAnnotationImageUpload');
  };

  const signOut = async (): Promise<boolean> => {
    setLoading(true);
    clearAll();

    await handleSessionExpired({ reason: 'user-initiated' });

    setLoading(false);
    return true;
  };

  const showEmailChangedToastAndPrefill = (
    onPrefillEmail: (email: string) => void
  ): void => {
    const params: URLSearchParams = new URLSearchParams(window.location.search);

    if (params.get('emailChanged') !== 'true') return;

    toast.success(
      'Email address updated successfully. Sign in with your new email.',
      {
        autoClose: 8000,
        position: 'top-center',
      }
    );

    const prefillEmail: string | null = params.get('email');

    if (prefillEmail) {
      onPrefillEmail(prefillEmail);
    }
  };

  // TODO: Outdated method of handling browser back button on auth.
  const blockBrowserBackOnSignIn = (): void => {
    const { href } = window.location;

    if (!href.includes('sign-in')) {
      return;
    }

    window.history.pushState(null, null, '');

    window.addEventListener('popstate', () => {
      window.history.pushState(null, null, '');
    });
  };

  useEffect(() => {
    setLoading(false);
    showEmailChangedToastAndPrefill(setEmail);
    blockBrowserBackOnSignIn();
  }, []);

  return {
    email,
    setEmail,

    password,
    setPassword,

    termsAndConditions,
    setTermsAndConditions,

    twoFACode,
    setTwoFACode,

    errors,
    show2FA,
    loading,

    submit,
    submitTwoFA,
    signOut,
    clearAuthStorage,
  };
};
